import { useState, useContext, useEffect } from "react";
import { CancelIcon, ICON_SIZE, SaveIcon } from "./actionIcons";
import ReactDOM from "react-dom";
import { GlobalContext } from "./GlobalContext";
import { Lock } from "lucide-react";
import ModalTitleBar from "./ModalTitleBar";
import { apiFetch } from './api.js';

function ChangePasswordModal({ isOpen, onClose, isFirstLogin = false }) {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toggleMessageBox, logOut, getMsgBoxResult, userData } = useContext(GlobalContext);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setError("");
      setSuccess(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Update escape key handler
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting && !isFirstLogin) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => document.removeEventListener('keydown', handleEscapeKey);
    }
  }, [isOpen, isSubmitting, isFirstLogin, onClose]);

  const handleClose = () => {
    if ( isFirstLogin || isSubmitting) return;
    onClose();
  };

  async function handleChangePassword() {
    if (isSubmitting) return;

    setError("");
    setSuccess(false);
    setIsSubmitting(true);

    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters long.");
      setIsSubmitting(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New password and confirm password do not match.");
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await apiFetch("/changepassword", { body: {
          EmailAddress: userData.EmailAddress,
          oldPassword,
          newPassword,
        } });

      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        const responseText = await response.text();
        throw new Error(`Unable to connect to the database. ${responseText}`);
      }

      const resObj = await response.json();

      if (response.ok) {
        if (resObj.ok) {
          setSuccess(true);
          setIsSubmitting(false);
          onClose();

          const clickedButton = await getMsgBoxResult({
            on: true,
            title: "Password Changed",
            message: "Please login with your new password.",
            buttons: ["OK"],
          });

          if (clickedButton !== "OK") console.error("Yo! WTF are you doing?");
          logOut();
        } else {
          setError(resObj.message || "Password change failed. Please try again.");
          setIsSubmitting(false);
        }
      } else {
        if (response.status === 401) {
          setIsSubmitting(false);
          toggleMessageBox({
            on: true,
            title: "Session Expired",
            message: "Your session has expired. Please log in again.",
            buttons: ["OK"],
          });
          onClose();
          logOut();
          return;
        }
        setError(resObj.message || "An error occurred while changing the password.");
        setIsSubmitting(false);
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred. Please try again.");
      setIsSubmitting(false);
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSubmitting && !isFirstLogin) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="modal-overlay"
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50
      }}
    >
      <div
        className="modal-content"
        style={{
          background: 'var(--theme-surface)',
          borderRadius: 'var(--theme-radius-lg)',
          overflow: 'hidden',
          width: '400px',
          maxWidth: '90vw',
          boxShadow: 'var(--theme-shadow-dialog)'
        }}
      >
        <ModalTitleBar
          icon={Lock}
          title={isFirstLogin ? "Set Your Password" : "Change Password"}
          onClose={isFirstLogin ? undefined : handleClose}
          disabled={isSubmitting}
        />
        <div className="modal-body-padded">
          {isFirstLogin && (
            <p className="hint-t" style={{ marginBottom: '16px' }}>
              Please change your temporary password to continue.
            </p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <input
              className="input-t"
              type="password"
              placeholder="Old Password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              disabled={isSubmitting}
            />
            <input
              className="input-t"
              type="password"
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isSubmitting}
            />
            <input
              className="input-t"
              type="password"
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          {error && (
            <p className="error-t" style={{ marginTop: '8px' }}>
              {error}
            </p>
          )}

          {success && (
            <p style={{ color: 'var(--theme-success)', fontSize: '14px', marginTop: '8px' }}>
              Password changed successfully!
            </p>
          )}
        </div>

        <div className="modal-shell-footer">
          {!isFirstLogin && (
            <button
              type="button"
              className="btn-t btn-t-secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            ><CancelIcon size={ICON_SIZE} />
              Cancel
            </button>
          )}
          <button
            type="button"
            className="btn-t btn-t-primary"
            onClick={handleChangePassword}
            disabled={isSubmitting}
          ><SaveIcon size={ICON_SIZE} />
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
    , document.body);
}

export default ChangePasswordModal;
